import React, { useState } from 'react'
import clsx from 'clsx'
import { Alert } from '@instacart/tds'
import TDSDialog from '~shared/lib/TDSDialog/TDSDialog'
import { AvailabilityIcon } from './AvailabilityIcon'
import styles from './Availability.module.scss'
import kdsStore from '../../Stores/KdsStore'
import { observer } from 'mobx-react'
import {
  DEFAULT_PREP_TIME_MINUTES,
  PREP_TIME_STEP_MINUTES,
  type DepartmentAvailabilityStatus,
  type SetDepartmentAvailabilityRequest,
} from '../../Types/DepartmentAvailability'

const STATUS_COPY: Record<DepartmentAvailabilityStatus, { title: string; subheading: string }> = {
  Online: { title: 'Online', subheading: 'Normal Ordering.' },
  Busy: { title: 'Busy', subheading: 'Additional prep time' },
  Offline: { title: 'Offline', subheading: 'No New Orders.' },
}

const StatusCard = ({
  status,
  onClick,
  selected,
}: {
  status: DepartmentAvailabilityStatus
  onClick: () => void
  selected: boolean
}) => (
  <div
    onClick={onClick}
    data-testid={`availability-status-${status}`}
    className={clsx(styles.departmentButton, selected && styles.active)}
  >
    <AvailabilityIcon status={status} />
    <div className={styles.departmentButtonText}>
      <div className={styles.departmentButtonTitle}>{STATUS_COPY[status].title}</div>
      <div className={styles.departmentButtonSubheading}>{STATUS_COPY[status].subheading}</div>
    </div>
  </div>
)

export const AvailabilityModal: React.FC<{
  setAvailabilityModalOpen: (isVisible: boolean) => void
  canAdjustDepartmentPrepTime?: boolean
}> = observer(({ setAvailabilityModalOpen, canAdjustDepartmentPrepTime = false }) => {
  const { updateDepartmentAvailabilityStatus, departmentStatus, additionalPrepMinutes } = kdsStore

  const [selectedStatus, setSelectedStatus] =
    useState<DepartmentAvailabilityStatus>(departmentStatus)
  const [prepMinutes, setPrepMinutes] = useState(additionalPrepMinutes || DEFAULT_PREP_TIME_MINUTES)

  const [warning, setWarning] = useState(false)
  const [error, setError] = useState(false)

  const isUnchanged =
    selectedStatus === departmentStatus &&
    (selectedStatus !== 'Busy' || prepMinutes === additionalPrepMinutes)

  const selectStatus = (status: DepartmentAvailabilityStatus) => {
    setSelectedStatus(status)
    setWarning(false)
    setError(false)
  }

  const submit = async () => {
    const request: SetDepartmentAvailabilityRequest =
      selectedStatus === 'Busy'
        ? { status: 'Busy', additionalPrepMinutes: prepMinutes }
        : { status: selectedStatus }

    try {
      // Routes through the shared PIN step-up flow. A dismissed PIN returns false (not a throw),
      // so keep the modal open without surfacing an error; a genuine failure throws.
      const changed = await updateDepartmentAvailabilityStatus(request)
      if (changed) {
        setAvailabilityModalOpen(false)
      }
    } catch {
      setError(true)
    }
    setWarning(false)
  }

  const handleUpdateAvailability = async (complete: () => void) => {
    // Taking a department Offline gets a confirm step; Online/Busy apply immediately. After a
    // failed attempt the operator has already confirmed, so "Try Again" retries the submit
    // directly rather than sending them back through the warning.
    if (selectedStatus === 'Offline' && !warning && !error) {
      setWarning(true)
    } else {
      await submit()
    }
    complete()
  }

  const renderBody = () => {
    if (error) {
      return (
        <Alert variant="error">
          <span>
            There was an error updating the department availability. Please try again later.
          </span>
        </Alert>
      )
    }

    if (warning) {
      return (
        <span>
          Taking this department offline will prevent customers from placing orders for the
          remainder of the day or until it is brought back online. Do you want to proceed?
        </span>
      )
    }

    const subheading =
      selectedStatus === 'Busy'
        ? 'Your storefront is active and accepting orders with extended prep times.'
        : selectedStatus === 'Online'
          ? 'Your storefront is active and accepting orders.'
          : 'Your storefront is offline and not accepting orders.'

    return (
      <>
        <Alert layout="horizontal" isDismissible={false} variant="warning">
          <span className={styles.warningMessage}>
            Store availability resets at the start of each day.
          </span>
        </Alert>
        <div className={styles.subheading}>{subheading}</div>
        <div className={styles.buttonContainer}>
          <StatusCard
            status="Online"
            onClick={() => selectStatus('Online')}
            selected={selectedStatus === 'Online'}
          />
          {canAdjustDepartmentPrepTime && (
            <StatusCard
              status="Busy"
              onClick={() => selectStatus('Busy')}
              selected={selectedStatus === 'Busy'}
            />
          )}
          <StatusCard
            status="Offline"
            onClick={() => selectStatus('Offline')}
            selected={selectedStatus === 'Offline'}
          />
        </div>
        {canAdjustDepartmentPrepTime && selectedStatus === 'Busy' && (
          <div className={styles.prepTimeSection}>
            <div className={styles.prepTimeQuestion}>
              How much additional prep time do you need?
            </div>
            <div className={styles.prepTimeRow}>
              <div className={styles.stepper}>
                <button
                  type="button"
                  aria-label="Decrease prep time"
                  className={styles.stepperButton}
                  disabled={prepMinutes <= PREP_TIME_STEP_MINUTES}
                  onClick={() =>
                    setPrepMinutes(m =>
                      Math.max(PREP_TIME_STEP_MINUTES, m - PREP_TIME_STEP_MINUTES)
                    )
                  }
                >
                  −
                </button>
                <span className={styles.stepperValue} data-testid="prep-minutes">
                  {prepMinutes}
                </span>
                <button
                  type="button"
                  aria-label="Increase prep time"
                  className={styles.stepperButton}
                  onClick={() => setPrepMinutes(m => m + PREP_TIME_STEP_MINUTES)}
                >
                  +
                </button>
              </div>
              <span className={styles.stepperUnit}>Minutes</span>
              <span className={styles.prepTimeHelp}>
                You will still receive orders with an additional prep time applied
              </span>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <TDSDialog
      toggleDialog={() => setAvailabilityModalOpen(false)}
      action={handleUpdateAvailability}
      header={warning ? 'Are you sure?' : 'Department Availability'}
      customClass={styles.availabilityModal}
      cancelButtonLabel="Cancel"
      actionButtonLabel={error ? 'Try Again' : warning ? 'Confirm' : 'Update Availability'}
      actionDisabled={!warning && isUnchanged}
    >
      <div>{renderBody()}</div>
    </TDSDialog>
  )
})

export default AvailabilityModal
